import { useCallback, useEffect, useRef, useState } from "react"
import { Result } from "@praha/byethrow"
import { tryAsync } from "@mylaunch/utils"

type AsyncFn<A extends unknown[], R> = (...args: A) => Promise<R>

export function useAsync<A extends unknown[], R>(fn: AsyncFn<A, R>) {
  const [loading, setLoading] = useState(false)
  const mountedRef = useRef(true)
  // Reset to true on mount handles React strict mode double-invoke (cleanup sets it false, re-mount must restore it)
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false } }, [])

  const run = useCallback((...args: A): Result.ResultAsync<R, string> => {
    setLoading(true)
    const stop = () => { if (mountedRef.current) setLoading(false) }
    return tryAsync(fn(...args), {
      catch: () => { stop(); return "server function call failed" as const },
      map: (v) => { stop(); return v },
    })
  }, [fn])

  return { loading, run }
}
